import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, fmtMoney, fmtDate, fmtNum, getToken, API } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Search, Download, Filter, ChevronLeft, ChevronRight, X, History, Pencil, Plus, Trash2 } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const COLS = [
  { key: "insured_name", label: "Insured", w: "min-w-[260px]" },
  { key: "carrier", label: "Carrier" },
  { key: "policy_number", label: "Policy #" },
  { key: "transaction_code", label: "Type" },
  { key: "business_segment", label: "Segment" },
  { key: "broker", label: "Broker", w: "min-w-[220px]" },
  { key: "line_of_biz", label: "LOB" },
  { key: "state", label: "State" },
  { key: "effective_date", label: "Effective", fmt: fmtDate },
  { key: "reported_gwp", label: "GWP", align: "right", fmt: fmtMoney },
  { key: "broker_commission", label: "Br Comm %", align: "right", fmt: (v) => v ? `${(v * 100).toFixed(2)}%` : "—" },
  { key: "ar_outstanding", label: "AR Out", align: "right", fmt: fmtMoney },
];

const NUMERIC_FIELDS = new Set([
  "reported_gwp", "broker_commission", "ar_outstanding", "policy_fees",
  "premium_tax", "mga_net_commission_prov", "mga_net_commission_exp",
  "policy_taxes", "athlete_safety_fee", "wholesale_broker_fee",
  "sl_tax", "sl_stamping_fee", "sl_other_fees", "provisional_commissions",
  "expected_commissions", "broker_comm_rpt",
]);

const AR_BUCKET_LABELS = {
  aging_current: "Current",
  aging_1_30: "1 – 30 days",
  aging_31_60: "31 – 60 days",
  aging_61_90: "61 – 90 days",
  aging_91_120: "91 – 120 days",
  aging_over_120: "120+ days",
};

export default function Policies() {
  const { user } = useAuth();
  const canWrite = user?.role === "admin" || user?.role === "controller";
  const [searchParams, setSearchParams] = useSearchParams();
  const arBucket = searchParams.get("ar_bucket") || "";
  const arOnly = searchParams.get("ar") === "outstanding";
  const stateParam = searchParams.get("state") || "";

  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(50);
  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState({
    carrier: "",
    business_segment: "",
    broker: "",
    state: "",
    transaction_code: "",
    line_of_biz: "",
  });
  const [filterOpts, setFilterOpts] = useState({});
  const [loading, setLoading] = useState(false);
  const [edit, setEdit] = useState(null); // selected policy
  const [creating, setCreating] = useState(false); // new-policy mode

  useEffect(() => {
    api.get("/policies/filters").then((r) => setFilterOpts(r.data)).catch(() => {});
  }, []);

  // pick up ?state=XX from URL (from the map drill-in)
  useEffect(() => {
    if (stateParam && stateParam !== filters.state) {
      setFilters((f) => ({ ...f, state: stateParam }));
      setPage(1);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stateParam]);

  // reset to page 1 when AR filter changes via URL
  useEffect(() => {
    setPage(1);
  }, [arBucket, arOnly]);

  const params = useMemo(() => {
    const p = { page, page_size: pageSize, sort: "reporting_date", sort_dir: "desc" };
    if (search.trim()) p.search = search.trim();
    Object.entries(filters).forEach(([k, v]) => {
      if (v) p[k] = v;
    });
    if (arBucket) p.ar_bucket = arBucket;
    if (arOnly) p.ar_outstanding_only = "true";
    return p;
  }, [page, pageSize, search, filters, arBucket, arOnly]);

  useEffect(() => {
    setLoading(true);
    api
      .get("/policies", { params })
      .then((r) => {
        setItems(r.data.items);
        setTotal(r.data.total);
      })
      .finally(() => setLoading(false));
  }, [params]);

  function clearFilters() {
    setFilters({
      carrier: "",
      business_segment: "",
      broker: "",
      state: "",
      transaction_code: "",
      line_of_biz: "",
    });
    setSearch("");
    setSearchParams({});
    setPage(1);
  }

  function clearArFilter() {
    const next = new URLSearchParams(searchParams);
    next.delete("ar_bucket");
    next.delete("ar");
    setSearchParams(next);
  }

  function exportXlsx() {
    const qs = new URLSearchParams();
    if (search.trim()) qs.set("search", search.trim());
    Object.entries(filters).forEach(([k, v]) => v && qs.set(k, v));
    if (arBucket) qs.set("ar_bucket", arBucket);
    if (arOnly) qs.set("ar_outstanding_only", "true");
    const t = getToken();
    fetch(`${API}/policies/export.xlsx?${qs.toString()}`, {
      headers: { Authorization: `Bearer ${t}` },
    })
      .then(async (r) => {
        if (!r.ok) throw new Error("Export failed");
        const blob = await r.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `mga_policies_${new Date().toISOString().slice(0, 10)}.xlsx`;
        a.click();
        window.URL.revokeObjectURL(url);
      })
      .catch((e) => toast.error(e.message));
  }

  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const activeFilterCount =
    Object.values(filters).filter(Boolean).length +
    (search.trim() ? 1 : 0) +
    (arBucket || arOnly ? 1 : 0);

  const arChipLabel = arBucket
    ? `AR Aging: ${AR_BUCKET_LABELS[arBucket] || arBucket}`
    : arOnly
    ? "AR Outstanding only"
    : null;

  return (
    <div className="space-y-4 max-w-[1500px]" data-testid="policies-page">
      {arChipLabel && (
        <div
          data-testid="ar-filter-chip"
          className="flex items-center justify-between bg-amber-50 border border-amber-200 rounded-lg px-4 py-2.5"
        >
          <div className="flex items-center gap-2">
            <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-amber-500 text-white">
              <Filter className="w-3 h-3" />
            </span>
            <div>
              <div className="text-[11px] uppercase tracking-wider text-amber-700 font-medium">
                Drill-in from dashboard
              </div>
              <div className="text-sm font-medium text-slate-900">{arChipLabel}</div>
            </div>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={clearArFilter}
            data-testid="ar-filter-clear"
            className="text-amber-700 hover:bg-amber-100 hover:text-amber-800 h-8"
          >
            <X className="w-4 h-4 mr-1" /> Remove
          </Button>
        </div>
      )}
      {/* Toolbar */}
      <Card className="p-4 border-slate-200">
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex-1 min-w-[260px]">
            <Label className="text-[11px] uppercase tracking-wider text-slate-400">Search</Label>
            <div className="relative mt-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                data-testid="policies-search-input"
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(1);
                }}
                placeholder="Insured, policy #, broker…"
                className="pl-9"
              />
            </div>
          </div>

          {[
            { key: "business_segment", label: "Segment" },
            { key: "carrier", label: "Carrier" },
            { key: "broker", label: "Broker" },
            { key: "transaction_code", label: "Type" },
            { key: "state", label: "State" },
            { key: "line_of_biz", label: "LOB" },
          ].map((f) => (
            <div key={f.key} className="min-w-[150px]">
              <Label className="text-[11px] uppercase tracking-wider text-slate-400">{f.label}</Label>
              <Select
                value={filters[f.key] || "__all__"}
                onValueChange={(v) => {
                  setFilters({ ...filters, [f.key]: v === "__all__" ? "" : v });
                  setPage(1);
                }}
              >
                <SelectTrigger className="mt-1 h-9" data-testid={`policies-filter-${f.key}`}>
                  <SelectValue placeholder="All" />
                </SelectTrigger>
                <SelectContent className="max-h-[300px]">
                  <SelectItem value="__all__">All</SelectItem>
                  {(filterOpts[f.key] || []).map((v) => (
                    <SelectItem key={v} value={v}>
                      {v}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          ))}

          <Button
            variant="outline"
            onClick={clearFilters}
            disabled={activeFilterCount === 0}
            data-testid="policies-clear-filters"
            className="h-9"
          >
            <X className="w-4 h-4 mr-1" /> Clear
          </Button>
          <Button
            variant="outline"
            onClick={exportXlsx}
            data-testid="policies-export-button"
            className="h-9"
          >
            <Download className="w-4 h-4 mr-1" /> Export
          </Button>
          {canWrite && (
            <Button
              onClick={() => setCreating(true)}
              data-testid="policies-new-button"
              className="h-9 bg-blue-600 hover:bg-blue-700"
            >
              <Plus className="w-4 h-4 mr-1" /> New policy
            </Button>
          )}
        </div>
      </Card>

      {/* Table */}
      <Card className="border-slate-200 overflow-hidden">
        <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between bg-slate-50/50">
          <div className="text-[12px] text-slate-600">
            <span className="font-medium text-slate-900">{fmtNum(total)}</span> records
            {activeFilterCount > 0 && (
              <span className="ml-2 inline-flex items-center gap-1 text-blue-600">
                <Filter className="w-3 h-3" /> {activeFilterCount} active filter
                {activeFilterCount > 1 ? "s" : ""}
              </span>
            )}
          </div>
          <div className="flex items-center gap-2 text-[12px] text-slate-500">
            <Button
              size="icon"
              variant="ghost"
              className="h-7 w-7"
              disabled={page === 1}
              onClick={() => setPage(page - 1)}
              data-testid="policies-page-prev"
            >
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <span>
              Page <b>{page}</b> / {totalPages}
            </span>
            <Button
              size="icon"
              variant="ghost"
              className="h-7 w-7"
              disabled={page >= totalPages}
              onClick={() => setPage(page + 1)}
              data-testid="policies-page-next"
            >
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-white">
              <tr className="text-left text-[11px] uppercase tracking-wider text-slate-400 border-b border-slate-200">
                {COLS.map((c) => (
                  <th
                    key={c.key}
                    className={`py-3 px-4 font-medium ${c.align === "right" ? "text-right" : ""} ${c.w || ""}`}
                  >
                    {c.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading && items.length === 0 && (
                <tr>
                  <td colSpan={COLS.length} className="py-12 text-center text-slate-400 text-sm">
                    Loading…
                  </td>
                </tr>
              )}
              {!loading && items.length === 0 && (
                <tr>
                  <td colSpan={COLS.length} className="py-12 text-center text-slate-400 text-sm">
                    No matching policies.
                  </td>
                </tr>
              )}
              {items.map((row) => (
                <tr
                  key={row.id}
                  data-testid={`policy-row-${row.id}`}
                  onClick={() => setEdit(row)}
                  className="border-b border-slate-100 last:border-0 hover:bg-blue-50/40 cursor-pointer transition-colors"
                >
                  {COLS.map((c) => {
                    const v = row[c.key];
                    const display = c.fmt ? c.fmt(v) : v ?? "—";
                    return (
                      <td
                        key={c.key}
                        className={`py-3 px-4 ${c.align === "right" ? "text-right tabular-nums" : ""} ${
                          c.key === "insured_name" ? "text-slate-900 font-medium" : "text-slate-600"
                        } truncate max-w-[300px]`}
                        title={typeof display === "string" ? display : undefined}
                      >
                        {display}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <PolicyEditSheet
        policy={edit}
        creating={creating}
        canWrite={canWrite}
        onClose={() => {
          setEdit(null);
          setCreating(false);
        }}
        onSaved={(updated, mode) => {
          if (mode === "create") {
            // refresh whole list to include new row in correct sort position
            api.get("/policies", { params }).then((r) => {
              setItems(r.data.items);
              setTotal(r.data.total);
            });
            setCreating(false);
            toast.success("Policy created");
          } else if (mode === "delete") {
            setItems((cur) => cur.filter((p) => p.id !== updated.id));
            setTotal((t) => Math.max(0, t - 1));
            setEdit(null);
            toast.success("Policy deleted");
          } else {
            setItems((cur) => cur.map((p) => (p.id === updated.id ? { ...p, ...updated } : p)));
            setEdit(null);
            toast.success("Policy updated");
          }
        }}
      />
    </div>
  );
}

function PolicyEditSheet({ policy, creating, canWrite, onClose, onSaved }) {
  const isCreate = !!creating && !policy;
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const [history, setHistory] = useState([]);
  const [tab, setTab] = useState("edit");
  const [historyLoading, setHistoryLoading] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  useEffect(() => {
    if (isCreate) {
      setDraft({
        insured_name: "", carrier: "", policy_number: "", transaction_code: "New",
        business_segment: "", broker: "", broker_code: "", line_of_biz: "GL", state: "",
        policy_type: "Single", reported_gwp: "", broker_commission: "",
        ar_outstanding: "", policy_fees: "", premium_tax: "",
        mga_net_commission_prov: "", mga_net_commission_exp: "",
        effective_date: "",
      });
      setHistory([]);
      setTab("edit");
    } else if (policy) {
      api.get(`/policies/${policy.id}`).then((r) => setDraft(r.data));
      setTab("edit");
      loadHistory(policy.id);
    } else {
      setDraft(null);
      setHistory([]);
    }
  }, [policy, isCreate]);

  async function loadHistory(id) {
    setHistoryLoading(true);
    try {
      const { data } = await api.get(`/policies/${id}/history`);
      setHistory(data);
    } catch {
      setHistory([]);
    } finally {
      setHistoryLoading(false);
    }
  }

  const open = !!policy || isCreate;
  if (!open) return null;

  const editableFields = [
    "insured_name", "carrier", "policy_number", "transaction_code",
    "business_segment", "broker", "broker_code", "line_of_biz", "state",
    "policy_type", "effective_date", "reported_gwp", "broker_commission",
    "ar_outstanding", "policy_fees", "premium_tax",
    "mga_net_commission_prov", "mga_net_commission_exp",
  ];

  function buildPatch() {
    const patch = {};
    editableFields.forEach((f) => {
      let v = draft[f];
      if (NUMERIC_FIELDS.has(f) && v !== "" && v != null) {
        v = Number(v);
        if (isNaN(v)) v = null;
      }
      patch[f] = v === "" ? null : v ?? null;
    });
    return patch;
  }

  async function save() {
    if (isCreate && !draft.insured_name && !draft.policy_number) {
      toast.error("Insured name or policy # is required");
      return;
    }
    setSaving(true);
    try {
      const patch = buildPatch();
      if (isCreate) {
        const { data } = await api.post(`/policies`, patch);
        onSaved(data, "create");
      } else {
        const { data } = await api.put(`/policies/${policy.id}`, patch);
        await loadHistory(policy.id);
        onSaved(data, "update");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally {
      setSaving(false);
    }
  }

  async function doDelete() {
    setSaving(true);
    try {
      await api.delete(`/policies/${policy.id}`);
      onSaved({ id: policy.id }, "delete");
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally {
      setSaving(false);
      setConfirmDelete(false);
    }
  }

  const titleText = isCreate ? "New policy" : canWrite ? "Policy detail" : "Policy detail (read-only)";
  const subtitle = isCreate
    ? "Add a single policy/transaction record"
    : `${draft?.policy_number || "—"} • ${draft?.insured_name || ""}`;

  return (
    <>
      <Sheet open={open} onOpenChange={(o) => !o && onClose()}>
        <SheetContent className="sm:max-w-xl overflow-y-auto" data-testid="policy-edit-sheet">
          <SheetHeader>
            <SheetTitle className="text-base">{titleText}</SheetTitle>
            <SheetDescription className="text-xs">{subtitle}</SheetDescription>
          </SheetHeader>
          {!draft && <div className="py-12 text-center text-slate-400">Loading…</div>}
          {draft && (
            <Tabs value={tab} onValueChange={setTab} className="mt-5">
              {!isCreate && (
                <TabsList className="grid grid-cols-2 w-full">
                  <TabsTrigger value="edit" data-testid="policy-tab-edit" className="text-xs">
                    <Pencil className="w-3.5 h-3.5 mr-1.5" /> Edit
                  </TabsTrigger>
                  <TabsTrigger value="history" data-testid="policy-tab-history" className="text-xs">
                    <History className="w-3.5 h-3.5 mr-1.5" />
                    History {history.length > 0 && `(${history.length})`}
                  </TabsTrigger>
                </TabsList>
              )}

              <TabsContent value="edit" className="mt-4 space-y-4">
                {editableFields.map((f) => (
                  <div key={f} className="space-y-1">
                    <Label className="text-[11px] uppercase tracking-wider text-slate-500">
                      {f.replace(/_/g, " ")}
                    </Label>
                    <Input
                      data-testid={`policy-edit-${f}`}
                      type={NUMERIC_FIELDS.has(f) ? "number" : "text"}
                      step="0.0001"
                      value={draft[f] ?? ""}
                      onChange={(e) => setDraft({ ...draft, [f]: e.target.value })}
                      disabled={!canWrite}
                    />
                  </div>
                ))}
                <div className="flex gap-2 pt-4 border-t border-slate-200 sticky bottom-0 bg-white items-center">
                  {canWrite && (
                    <Button
                      onClick={save}
                      disabled={saving}
                      className="bg-blue-600 hover:bg-blue-700"
                      data-testid="policy-edit-save"
                    >
                      {saving ? "Saving…" : isCreate ? "Create policy" : "Save changes"}
                    </Button>
                  )}
                  <Button variant="outline" onClick={onClose} data-testid="policy-edit-cancel">
                    {canWrite ? "Cancel" : "Close"}
                  </Button>
                  {!isCreate && canWrite && (
                    <Button
                      variant="ghost"
                      onClick={() => setConfirmDelete(true)}
                      data-testid="policy-edit-delete"
                      className="ml-auto text-red-600 hover:text-red-700 hover:bg-red-50"
                    >
                      <Trash2 className="w-4 h-4 mr-1" /> Delete
                    </Button>
                  )}
                </div>
              </TabsContent>

              {!isCreate && (
                <TabsContent value="history" className="mt-4">
                  {historyLoading && (
                    <div className="py-12 text-center text-slate-400 text-sm">Loading…</div>
                  )}
                  {!historyLoading && history.length === 0 && (
                    <div className="py-12 text-center" data-testid="history-empty">
                      <History className="w-8 h-8 mx-auto text-slate-300" strokeWidth={1.5} />
                      <div className="text-sm text-slate-500 mt-2">No edits recorded yet.</div>
                      <div className="text-[11px] text-slate-400 mt-1">
                        Edits made from this panel will appear here automatically.
                      </div>
                    </div>
                  )}
                  {!historyLoading && history.length > 0 && (
                    <ol className="relative border-l border-slate-200 ml-2 space-y-5" data-testid="history-list">
                      {history.map((h, idx) => (
                        <li key={idx} className="ml-4">
                          <div className="absolute -left-[5px] w-2.5 h-2.5 rounded-full bg-blue-500 ring-4 ring-blue-100 mt-1.5" />
                          <div className="flex items-baseline justify-between gap-2">
                            <div className="text-[12px] font-medium text-slate-900">
                              {h.user_name || h.user_email}
                            </div>
                            <time className="text-[11px] text-slate-400 whitespace-nowrap">
                              {fmtAuditTime(h.at)}
                            </time>
                          </div>
                          <div className="text-[11px] text-slate-500 mb-2">
                            {h.action === "create"
                              ? "Created policy"
                              : `${Object.keys(h.changes || {}).length} field${Object.keys(h.changes || {}).length !== 1 ? "s" : ""} changed`}
                          </div>
                          {h.action !== "create" && (
                            <ul className="space-y-1.5">
                              {Object.entries(h.changes || {}).map(([fld, diff]) => (
                                <li
                                  key={fld}
                                  className="text-[12px] bg-slate-50 border border-slate-200 rounded-md px-2.5 py-1.5"
                                >
                                  <div className="text-[10px] uppercase tracking-wider text-slate-400 font-medium">
                                    {fld.replace(/_/g, " ")}
                                  </div>
                                  <div className="flex items-center gap-1.5 flex-wrap">
                                    <span className="text-rose-700 line-through tabular-nums max-w-[140px] truncate">
                                      {fmtAuditValue(diff.from)}
                                    </span>
                                    <span className="text-slate-400">→</span>
                                    <span className="text-emerald-700 font-medium tabular-nums max-w-[140px] truncate">
                                      {fmtAuditValue(diff.to)}
                                    </span>
                                  </div>
                                </li>
                              ))}
                            </ul>
                          )}
                        </li>
                      ))}
                    </ol>
                  )}
                </TabsContent>
              )}
            </Tabs>
          )}
        </SheetContent>
      </Sheet>

      <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this policy?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently removes <b>{draft?.insured_name || draft?.policy_number || "the record"}</b>. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="policy-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={doDelete}
              data-testid="policy-delete-confirm"
              className="bg-red-600 hover:bg-red-700"
            >
              Delete policy
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

function fmtAuditValue(v) {
  if (v == null || v === "") return "—";
  if (typeof v === "number") return v.toLocaleString("en-US", { maximumFractionDigits: 4 });
  if (typeof v === "string" && /^\d{4}-\d{2}-\d{2}T/.test(v)) {
    try {
      return new Date(v).toLocaleDateString();
    } catch {
      return v;
    }
  }
  return String(v);
}

function fmtAuditTime(v) {
  if (!v) return "";
  try {
    const d = new Date(v);
    return d.toLocaleString("en-US", {
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return v;
  }
}
